/** In-memory tour hold: profile stays "processing" until reveal. */

type HoldEntry = {
  /** Title shown in header/sidebar while held (usually creation timestamp). */
  displayTitle: string;
  /** FIO from completed task; applied to list title on reveal. */
  pendingName?: string;
};

let holdById = new Map<string, HoldEntry>();

export function beginTourProcessingHold(
  profileId: string,
  displayTitle: string,
): void {
  if (!profileId) return;
  holdById.set(profileId, { displayTitle });
}

export function isTourProcessingHold(profileId: string): boolean {
  return holdById.has(profileId);
}

export function getTourHoldDisplayTitle(profileId: string): string | undefined {
  return holdById.get(profileId)?.displayTitle;
}

export function getTourHoldPendingName(profileId: string): string | undefined {
  return holdById.get(profileId)?.pendingName;
}

export function noteTourHoldPendingName(
  profileId: string,
  pendingName: string,
): void {
  const entry = holdById.get(profileId);
  if (!entry || !pendingName) return;
  holdById.set(profileId, { ...entry, pendingName });
}

export function noteTourHoldRenamed(fromId: string, toId: string): void {
  const entry = holdById.get(fromId);
  if (!entry) return;
  holdById.delete(fromId);
  if (toId) holdById.set(toId, entry);
}

export function clearTourProcessingHold(profileId?: string): void {
  if (profileId) {
    holdById.delete(profileId);
    return;
  }
  holdById = new Map();
}

export type TourHeldProfile = {
  id: string;
  displayTitle: string;
  pendingName?: string;
};

export function takeTourHeldProfiles(): TourHeldProfile[] {
  const items = [...holdById.entries()].map(([id, entry]) => ({
    id,
    displayTitle: entry.displayTitle,
    pendingName: entry.pendingName,
  }));
  holdById = new Map();
  return items;
}

/** @deprecated Prefer takeTourHeldProfiles — kept for call sites that only need ids. */
export function takeTourHeldProfileIds(): string[] {
  return takeTourHeldProfiles().map((p) => p.id);
}

export function peekTourHeldProfileIds(): string[] {
  return [...holdById.keys()];
}
